import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import CallButton from './CallButton';
import { IMAGE_ASSETS } from '../constants/images';
import { useLanguage } from '../context/LanguageContext';

const { FiMenu, FiX, FiGlobe } = FiIcons;

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { lang, toggleLanguage, t } = useLanguage();
  const location = useLocation();

  const links = [
    { name: t('nav.home'), path: '/' },
    { name: t('nav.menu'), path: '/menu' },
    { name: t('nav.poolEvents'), path: '/pool-events' },
    { name: t('nav.gallery'), path: '/gallery' },
    { name: t('nav.about'), path: '/about' },
    { name: t('nav.contact'), path: '/contact' },
  ];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  return (
    <header className={`fixed w-full top-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md py-2' : 'bg-white/95 backdrop-blur-sm py-4'}`}>
      <div className="container mx-auto px-4 md:px-6 flex justify-between items-center">
        <Link to="/" className="flex items-center gap-2 z-50">
          <img src={IMAGE_ASSETS.logo} alt="Johnny's Bar Billard" className="h-12 md:h-14 w-auto object-contain rounded-lg shadow-sm" />
        </Link>

        <nav className="hidden lg:flex items-center gap-6">
          {links.map((link) => (
            <Link key={link.path} to={link.path} className={`font-medium text-sm transition-colors hover:text-brand-red relative group ${location.pathname === link.path ? 'text-brand-red' : 'text-brand-navy'}`}>
              {link.name}
              <span className={`absolute -bottom-1 left-0 w-full h-0.5 bg-brand-red transform origin-left transition-transform duration-300 ${location.pathname === link.path ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'}`}></span>
            </Link>
          ))}
          
          <button 
            onClick={toggleLanguage}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-200 text-xs font-bold text-brand-navy hover:bg-gray-50 transition-colors uppercase tracking-wider"
          >
            <SafeIcon icon={FiGlobe} className="text-sm text-brand-red" />
            {lang === 'en' ? 'FR' : 'EN'}
          </button>

          <CallButton className="px-5 py-2 text-sm">
            {t('common.reserve')}
          </CallButton>
        </nav>

        <div className="flex items-center gap-4 lg:hidden z-50">
          <button 
            onClick={toggleLanguage}
            className="flex items-center gap-1 px-2 py-1 rounded-md border border-gray-200 text-xs font-bold text-brand-navy"
          >
            {lang === 'en' ? 'FR' : 'EN'}
          </button>
          <button className="text-brand-navy p-2" onClick={() => setIsOpen(!isOpen)}>
            <SafeIcon icon={isOpen ? FiX : FiMenu} className="text-3xl" />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="absolute top-full left-0 w-full bg-white shadow-xl border-t border-gray-100 lg:hidden">
            <nav className="flex flex-col p-6 gap-4">
              {links.map((link) => (
                <Link key={link.path} to={link.path} className={`text-lg font-medium py-2 border-b border-gray-50 ${location.pathname === link.path ? 'text-brand-red' : 'text-brand-navy'}`}>
                  {link.name}
                </Link>
              ))}
              <div className="pt-4 flex justify-center">
                <CallButton className="w-full">
                  {t('common.reserve')}
                </CallButton>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}